import React from 'react';
import { Link } from 'react-router-dom';

import SubtleBadge from 'components/common/SubtleBadge';

import paths from 'routes/paths';
import { TIME_OPTION } from 'constants/time';

export const getProductListColumns = (tenant, warehouse) => [
  {
    accessorKey: 'name',
    header: 'Name',
    enableSorting: true,
    meta: {
      valueType: 'string',
      truncate: true,
      minWidth: '300px',
      maxWidth: '300px',
    },
    cell: ({ row: { original } }) => {
      const { name } = original;

      return (
        <span className="d-block text-truncate" title={name}>
          {name}
        </span>
      );
    },
  },
  {
    accessorKey: 'sku',
    header: 'SKU Code',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { id, sku } = original;

      return (
        <Link to={paths.products.details(tenant, warehouse, id)}>
          <strong>{sku}</strong>
        </Link>
      );
    },
  },
  {
    accessorKey: 'brand__name',
    header: 'Brand',
    enableSorting: true,
    meta: {
      valueType: 'string',
      truncate: true,
      minWidth: '120px',
      maxWidth: '120px',
    },
    cell: ({ row: { original } }) => {
      const { brand } = original;

      return (
        <span className="d-block text-truncate" title={brand.name}>
          {brand.name}
        </span>
      );
    },
  },
  {
    accessorKey: 'barcode',
    header: 'Barcode',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
  },
  {
    accessorKey: 'tax_type',
    header: 'Tax Rate',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { tax_type } = original;

      return <span>{tax_type.tax_rate}</span>;
    },
  },
  {
    accessorKey: 'is_active',
    header: 'Enabled',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { is_active } = original;

      return (
        <SubtleBadge pill bg={is_active ? 'success' : 'secondary'}>
          {is_active ? 'Active' : 'InActive'}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'created',
    header: 'Created',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { created } = original;

      return (
        <span>{new Date(created).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
];

export const getChannelProductListColumns = (tenant, warehouse) => [
  {
    accessorKey: 'channel__channel_type',
    header: 'Channel',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { channel } = original;

      return <span>{channel.channel_type}</span>;
    },
  },
  {
    accessorKey: 'product',
    header: 'SKU Summary',
    enableSorting: false,
    meta: {
      cellProps: { className: 'py-2' },
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { product, channel_product_key, channel_product_sku } = original;

      return (
        <>
          {product ? (
            <div className="mb-2">
              <strong>SKU Code: </strong>
              <span>{product?.sku || '-'}</span>
            </div>
          ) : null}
          <div className="mb-2">
            <strong>Seller SKU on Channel: </strong>
            <span>{channel_product_sku || '-'}</span>
          </div>
          <div>
            <strong>Channel Product Id: </strong>
            <span>{channel_product_key}</span>
          </div>
        </>
      );
    },
  },
  {
    accessorKey: 'channel_product_name',
    header: 'Product Name on Channel',
    enableSorting: false,
    meta: {
      cellProps: { className: 'py-2' },
      valueType: 'string',
      truncate: true,
      minWidth: '300px',
      maxWidth: '300px',
    },
    cell: ({ row: { original } }) => {
      const { channel_product_name, channel_product_link } = original;

      return (
        <a
          href={channel_product_link}
          target="_blank"
          rel="noopener noreferrer"
          className="d-block text-truncate"
        >
          <strong>{channel_product_name}</strong>
        </a>
      );
    },
  },
  {
    accessorKey: 'batch_quantity',
    header: 'Next Inventory Update',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { batch_quantity } = original;
      return <span>{batch_quantity.toLocaleString()}</span>;
    },
  },
  {
    accessorKey: 'batch_allocated',
    header: 'Blocked Inventory',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { batch_allocated } = original;
      return <span>{batch_allocated.toLocaleString()}</span>;
    },
  },
  {
    accessorKey: 'quantity',
    header: 'Last Inventory Update',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { quantity } = original;
      return <span>{quantity.toLocaleString()}</span>;
    },
  },
  {
    accessorKey: 'quantity_allocated',
    header: 'Last Blocked Inventory Update',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { quantity_allocated } = original;
      return <span>{quantity_allocated.toLocaleString()}</span>;
    },
  },
  {
    accessorKey: 'channel_product_live',
    header: 'Listing Status',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { channel_product_live } = original;

      return (
        <SubtleBadge pill bg={channel_product_live ? 'success' : 'secondary'}>
          {channel_product_live ? 'Active' : 'InActive'}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'created',
    header: 'Created',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { created } = original;

      return (
        <span>{new Date(created).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
];
